import { useQuery } from "react-query";
import { Clock, FileJson, Globe, Hash, Layers, MapPin, Monitor, ScrollText, Target, UserRound } from "lucide-react";
import { saasApi, saasErrorMessage } from "../shared/saasApi";
import { categoryLabel, formatDateTime, humanize, LOG_SOURCE_LABELS } from "../shared/saasFormat";
import DetailModal, { DetailChip, DetailEmpty, DetailField, DetailOverview, DetailPanel, DetailSection } from "./DetailModal";
import { WorkspaceContext } from "./workspaceContext";

export default function EventDetailDialog({ context, eventId, onClose }: { context: WorkspaceContext; eventId: number; onClose: () => void }) {
    const query = useQuery(["saas-event", context.provider, context.tenantName, eventId], () => saasApi.event(context.provider, context.tenantName, eventId));
    const event = query.data;
    const risky = event?.riskLevel === "HIGH" || event?.riskLevel === "CRITICAL";

    return (
        <DetailModal
            onClose={onClose}
            icon={<ScrollText />}
            title={<span className="font-mono">{event?.action ?? "Event"}</span>}
            subtitle={event?.summary ?? undefined}
            chips={event && (
                <>
                    {event.riskLevel && <DetailChip tone={risky ? "severity" : "type"}>{humanize(event.riskLevel)}{event.riskScore !== null ? ` · ${event.riskScore}` : ""}</DetailChip>}
                    {event.category && <DetailChip>{categoryLabel(event.category)}</DetailChip>}
                    {event.logSource && <DetailChip>{LOG_SOURCE_LABELS[event.logSource]}</DetailChip>}
                </>
            )}
        >
            {query.isLoading ? (
                <div className="h-40 rounded-xl border border-border animate-pulse" />
            ) : query.isError || !event ? (
                <DetailEmpty>{saasErrorMessage(query.error, "Unable to load this event")}</DetailEmpty>
            ) : (
                <>
                    <DetailOverview>
                        <DetailField icon={<Clock />} label="Time" value={formatDateTime(event.occurredAt)} />
                        <DetailField
                            icon={<UserRound />}
                            label="Actor"
                            value={event.actor ? (
                                <button type="button" className="text-primary hover:underline"
                                        onClick={() => {
                                            onClose();
                                            context.openIdentity(event.connectionId, event.actor as string);
                                        }}>
                                    {event.actor}
                                </button>
                            ) : "—"}
                        />
                        <DetailField icon={<MapPin />} label="IP address" value={event.ipAddress ? `${event.ipAddress}${event.country ? ` (${event.country})` : ""}` : "—"} mono />
                        <DetailField icon={<Layers />} label="Resource" value={event.resourceName ?? "—"} title={event.resourceName ?? undefined} />
                        <DetailField icon={<Target />} label="Target" value={event.targetName ?? "—"} title={event.targetName ?? undefined} />
                        <DetailField icon={<Globe />} label="Scope" value={event.scopeName ?? "—"} />
                        <DetailField icon={<Hash />} label="Event type" value={event.eventType ?? "—"} />
                        <DetailField icon={<Monitor />} label="User agent" value={event.userAgent ?? "—"} title={event.userAgent ?? undefined} />
                    </DetailOverview>
                    <DetailSection icon={<FileJson />} title="Raw event">
                        <DetailPanel>
                            <pre className="saas-json">{JSON.stringify(event.raw, null, 2)}</pre>
                        </DetailPanel>
                    </DetailSection>
                </>
            )}
        </DetailModal>
    );
}
